// ============================================================================
// Field Registry Types - Arquitetura Pipedrive-like de campos
// ============================================================================

import type { FormulaCalc } from '@/lib/campoCalculado/operadores';

export type EntityType =
  | 'deals' | 'companies' | 'leads' | 'activities' | 'work_items' | 'client_contracts';

export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'select'
  | 'textarea'
  | 'phone'
  | 'email_field'
  | 'monetary'
  | 'multi_select'
  | 'user'
  | 'address'
  | 'autocomplete'
  | 'time'
  | 'time_range'
  | 'entity_reference'
  | 'labels'
  | 'list'
  | 'attachment'
  // Documentos brasileiros. São tipos próprios (e não 'text') para que a
  // máscara e a validação valham para qualquer organização, independente do
  // nome que ela dê ao campo — igual ao que 'phone' e 'monetary' já fazem.
  | 'cpf'
  | 'cep'
  | 'rg'
  // Estado brasileiro: select tolerante com os 27 nomes oficiais embutidos.
  | 'estado'
  // Campo calculado: o valor (número) é a soma de regras de pontos, gravada em custom_data pelo
  // trigger zzz_campo_calc. SOMENTE LEITURA em toda a UI. Ver docs/specs/campo-calculado.md.
  | 'calculated';

// ============================================================================
// Core interfaces
// ============================================================================

export interface FieldGroup {
  id: string;
  org_id: string;
  entity_type: EntityType;
  name: string;
  display_order: number;
  is_collapsible: boolean;
  is_default_collapsed: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface FieldDefinition {
  id: string;
  org_id: string;
  field_name: string;
  field_type: FieldType;
  field_label: string;
  field_placeholder?: string | null;
  field_options?: string[] | null;
  is_required: boolean;
  display_order: number;
  is_active: boolean;
  page_type: EntityType;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  // Novos campos do registry
  is_system: boolean;
  system_column: string | null;
  group_id: string | null;
  is_visible_in_add_form: boolean;
  is_visible_in_detail: boolean;
  is_visible_in_list: boolean;
  is_filterable: boolean;
  is_sortable: boolean;
  is_important: boolean;
  reference_entity: EntityType | null;
  is_multi_value: boolean;
  value_labels: string[] | null;
  /** Só em field_type = 'calculated'. Normalizada pelo banco (trg_campo_calc_formula). */
  formula?: FormulaCalc | null;
}

export interface FieldGroupWithFields {
  group: FieldGroup;
  fields: FieldDefinition[];
}

// ============================================================================
// Tipos para valores complexos
// ============================================================================

export interface MultiValueEntry {
  value: string;
  label: string;
  is_primary: boolean;
}

export interface ListItem {
  key: string;
  value: string;
}

/** Valor de um campo do tipo 'attachment' — guardado como array em custom_data. */
export interface AttachmentValue {
  /** Caminho no bucket privado 'custom-field-files' (gera URL assinada sob demanda). */
  path: string;
  /** Nome original do arquivo. */
  name: string;
  /** Tamanho em bytes. */
  size: number;
  /** MIME type. */
  type: string;
  /** ISO timestamp do upload. */
  uploaded_at: string;
}

export interface AddressValue {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface TimeRangeValue {
  start: string;
  end: string;
}

interface LabelEntry {
  text: string;
  color: string;
}

// ============================================================================
// DTOs para CRUD
// ============================================================================

export interface CreateFieldData {
  field_name: string;
  field_type: FieldType;
  field_label: string;
  field_placeholder?: string;
  field_options?: string[];
  is_required?: boolean;
  display_order?: number;
  page_type: EntityType;
  /** `null` = sem grupo (cai no bucket virtual "Outros"). */
  group_id?: string | null;
  is_visible_in_add_form?: boolean;
  is_visible_in_detail?: boolean;
  is_visible_in_list?: boolean;
  is_filterable?: boolean;
  is_sortable?: boolean;
  is_important?: boolean;
  reference_entity?: EntityType;
  is_multi_value?: boolean;
  value_labels?: string[];
  formula?: FormulaCalc | null;
}

export interface UpdateFieldData extends Partial<CreateFieldData> {
  id: string;
}

export interface CreateFieldGroupData {
  entity_type: EntityType;
  name: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_collapsed?: boolean;
}

export interface UpdateFieldGroupData extends Partial<CreateFieldGroupData> {
  id: string;
}

export interface FieldVisibilityUpdate {
  id: string;
  is_visible_in_add_form?: boolean;
  is_visible_in_detail?: boolean;
  is_visible_in_list?: boolean;
  is_filterable?: boolean;
  is_sortable?: boolean;
}

// ============================================================================
// Tipos para integração com colunas e filtros
// ============================================================================

export interface FieldColumnDefinition {
  id: string;
  label: string;
  defaultVisible: boolean;
  required?: boolean;
  fieldType: FieldType;
  isSystem: boolean;
  systemColumn: string | null;
  fieldName: string;
  sortable: boolean;
  width?: number;
}

export interface FieldFilterOption {
  value: string;
  label: string;
  type: FieldType;
  options?: string[];
  isSystem: boolean;
}

// ============================================================================
// Constantes de configuração
// ============================================================================

export const FIELD_TYPE_CONFIG: Record<FieldType, { label: string; icon: string; category: 'basic' | 'advanced' | 'reference' }> = {
  text: { label: 'Texto', icon: 'Type', category: 'basic' },
  number: { label: 'Número', icon: 'Hash', category: 'basic' },
  date: { label: 'Data', icon: 'Calendar', category: 'basic' },
  select: { label: 'Seleção', icon: 'ChevronDown', category: 'basic' },
  textarea: { label: 'Texto Longo', icon: 'AlignLeft', category: 'basic' },
  phone: { label: 'Telefone', icon: 'Phone', category: 'advanced' },
  email_field: { label: 'E-mail', icon: 'Mail', category: 'advanced' },
  monetary: { label: 'Monetário', icon: 'DollarSign', category: 'advanced' },
  multi_select: { label: 'Seleção Múltipla', icon: 'ListChecks', category: 'advanced' },
  user: { label: 'Usuário', icon: 'User', category: 'reference' },
  address: { label: 'Endereço', icon: 'MapPin', category: 'advanced' },
  autocomplete: { label: 'Auto-completar', icon: 'Search', category: 'advanced' },
  time: { label: 'Hora', icon: 'Clock', category: 'basic' },
  time_range: { label: 'Período', icon: 'Timer', category: 'advanced' },
  entity_reference: { label: 'Referência', icon: 'Link', category: 'reference' },
  labels: { label: 'Etiquetas', icon: 'Tags', category: 'advanced' },
  list: { label: 'Lista', icon: 'List', category: 'advanced' },
  attachment: { label: 'Anexo', icon: 'Paperclip', category: 'advanced' },
  cpf: { label: 'CPF', icon: 'IdCard', category: 'advanced' },
  cep: { label: 'CEP', icon: 'MapPin', category: 'advanced' },
  rg: { label: 'RG', icon: 'IdCard', category: 'advanced' },
  estado: { label: 'Estado (UF)', icon: 'MapPin', category: 'advanced' },
  calculated: { label: 'Calculado', icon: 'Calculator', category: 'advanced' },
};

export const PHONE_LABELS = ['Celular', 'Trabalho', 'Casa', 'Outro'];
export const EMAIL_LABELS = ['Trabalho', 'Pessoal', 'Outro'];

const DEFAULT_COLUMN_WIDTHS: Record<FieldType, number> = {
  text: 180,
  number: 120,
  date: 140,
  select: 150,
  textarea: 220,
  phone: 160,
  email_field: 200,
  monetary: 140,
  multi_select: 180,
  user: 160,
  address: 220,
  autocomplete: 180,
  time: 100,
  time_range: 160,
  entity_reference: 180,
  labels: 180,
  list: 280,
  attachment: 220,
  cpf: 150,
  cep: 120,
  rg: 140,
  estado: 170,
  calculated: 120,
};

// ============================================================================
// Filtros: presets de data relativa só na coluna de valor; demais no seletor de condição
// ============================================================================

/** Presets de data relativa (filterEngine) — ficam fora do seletor de condição. */
export const DATE_RELATIVE_OPERATORS = new Set<string>([
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "this_year",
  "last_year",
]);

type FilterValueKind = "literal" | "relative_date";

function getFilterValueKind(fieldType: FieldType, operator: string): FilterValueKind {
  if (fieldType === "date" && DATE_RELATIVE_OPERATORS.has(operator)) return "relative_date";
  return "literal";
}

/** Regra legada: `operador` era o preset (ex.: this_month) e `valor` vazio. Converte para equals + valor = preset. */
export function normalizeLegacyDateFilterCondition<T extends { operator: string; value: string }>(c: T): T {
  if (!DATE_RELATIVE_OPERATORS.has(c.operator)) return c;
  if (!c.value || c.value === "") {
    return { ...c, operator: "equals", value: c.operator };
  }
  return { ...c, operator: "equals", value: c.value };
}

// Operadores por tipo de campo para uso nos filtros
export const FIELD_TYPE_OPERATORS: Record<string, { value: string; label: string }[]> = {
  text: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  textarea: [
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  number: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'greater_than', label: 'maior que' },
    { value: 'less_than', label: 'menor que' },
    { value: 'greater_than_or_equal', label: 'maior ou igual a' },
    { value: 'less_than_or_equal', label: 'menor ou igual a' },
    { value: 'between', label: 'entre' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  monetary: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'greater_than', label: 'maior que' },
    { value: 'less_than', label: 'menor que' },
    { value: 'between', label: 'entre' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  date: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'after', label: 'depois de' },
    { value: 'before', label: 'antes de' },
    { value: 'between', label: 'entre' },
    { value: 'today', label: 'hoje' },
    { value: 'yesterday', label: 'ontem' },
    { value: 'this_week', label: 'esta semana' },
    { value: 'this_month', label: 'este mês' },
    { value: 'this_year', label: 'este ano' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  select: [
    { value: 'equals', label: 'é um de' },
    { value: 'not_equals', label: 'não é um de' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  multi_select: [
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  phone: [
    { value: 'contains', label: 'contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  email_field: [
    { value: 'equals', label: 'é igual a' },
    { value: 'contains', label: 'contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  user: [
    { value: 'equals', label: 'é um de' },
    { value: 'not_equals', label: 'não é um de' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  entity_reference: [
    { value: 'equals', label: 'é um de' },
    { value: 'not_equals', label: 'não é um de' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  labels: [
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  address: [
    { value: 'contains', label: 'contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  autocomplete: [
    { value: 'equals', label: 'é igual a' },
    { value: 'contains', label: 'contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  time: [
    { value: 'equals', label: 'é igual a' },
    { value: 'after', label: 'depois de' },
    { value: 'before', label: 'antes de' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  time_range: [
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  list: [
    { value: 'contains', label: 'contém' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  attachment: [
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
  // Valor numérico em custom_data: filtra como número.
  calculated: [
    { value: 'equals', label: 'é igual a' },
    { value: 'not_equals', label: 'não é igual a' },
    { value: 'greater_than', label: 'maior que' },
    { value: 'less_than', label: 'menor que' },
    { value: 'greater_than_or_equal', label: 'maior ou igual a' },
    { value: 'less_than_or_equal', label: 'menor ou igual a' },
    { value: 'between', label: 'entre' },
    { value: 'is_empty', label: 'está vazio' },
    { value: 'is_not_empty', label: 'não está vazio' },
  ],
};

/** Operadores no seletor central: inclui vazio/não vazio; exclui só presets de data relativa. */
export function getComparisonOperatorsForField(
  fieldType: FieldType
): { value: string; label: string }[] {
  if (fieldType === 'labels') {
    return [
      { value: 'contains', label: 'contém' },
      { value: 'not_contains', label: 'não contém' },
      { value: 'is_empty', label: 'está vazio' },
      { value: 'is_not_empty', label: 'não está vazio' },
    ];
  }
  const base = FIELD_TYPE_OPERATORS[fieldType] || FIELD_TYPE_OPERATORS.text;
  return base.filter((op) => !DATE_RELATIVE_OPERATORS.has(op.value));
}

export function defaultComparisonOperatorForField(fieldType: FieldType): string {
  if (fieldType === 'labels') return 'contains';
  if (fieldType === 'time_range') return 'is_empty';
  const ops = getComparisonOperatorsForField(fieldType);
  return ops[0]?.value ?? 'equals';
}

// Mapeamento de entity_type para label PT-BR
/**
 * Entidades que NÃO aceitam campo personalizado.
 *
 * ⚠️ Campo personalizado guarda o valor em `<tabela>.custom_data` (jsonb). `client_contracts`
 * não tem essa coluna — foi desenhada com colunas reais, não com bolsa de jsonb. Criar um campo
 * ali gravaria a DEFINIÇÃO em `fields` e não teria onde gravar o VALOR: o campo apareceria nos
 * formulários e perderia tudo que fosse digitado, em silêncio.
 *
 * Ela entra em `EntityType` mesmo assim porque FILTRO por coluna própria funciona — é só a
 * personalização que não. Enquanto não houver `custom_data`, esta lista é o que impede a aba de
 * aparecer em Configurações › Campos de dados, que monta as abas iterando `ENTITY_TYPE_LABELS`.
 */
export const ENTITIES_SEM_CAMPOS_PERSONALIZADOS = new Set<EntityType>(['client_contracts']);

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  deals: 'Negócios',
  companies: 'Empresas',
  leads: 'Leads',
  activities: 'Atividades',
  work_items: 'Projetos',
  client_contracts: 'Contratos',
};

// ============================================================================
// Campos default do sistema (fallback frontend)
// Usados quando a migração seed_system_fields ainda não rodou no banco.
// Cada campo usa um ID determinístico baseado em entity+field_name para
// permitir deduplicação com campos reais do banco.
// ============================================================================

type DefaultFieldSeed = Pick<FieldDefinition,
  'field_name' | 'field_type' | 'field_label' | 'system_column' | 'display_order' |
  'is_visible_in_list' | 'is_sortable' | 'is_filterable' |
  'is_visible_in_add_form' | 'is_visible_in_detail'
>;

function buildDefaultField(
  entityType: EntityType,
  seed: DefaultFieldSeed,
  orgId: string,
  groupId: string,
): FieldDefinition {
  return {
    id: `__default_${entityType}_${seed.field_name}__`,
    org_id: orgId,
    field_name: seed.field_name,
    field_type: seed.field_type,
    field_label: seed.field_label,
    field_placeholder: null,
    field_options: null,
    is_required: false,
    display_order: seed.display_order,
    is_active: true,
    page_type: entityType,
    created_by: null,
    created_at: '',
    updated_at: '',
    is_system: true,
    system_column: seed.system_column,
    group_id: groupId,
    is_visible_in_add_form: seed.is_visible_in_add_form,
    is_visible_in_detail: seed.is_visible_in_detail,
    is_visible_in_list: seed.is_visible_in_list,
    is_filterable: seed.is_filterable,
    is_sortable: seed.is_sortable,
    is_important: false,
    reference_entity: null,
    is_multi_value: false,
    value_labels: null,
  };
}

const LEADS_SYSTEM_SEEDS: DefaultFieldSeed[] = [
  { field_name: 'sys_name', field_type: 'text', field_label: 'Nome', system_column: 'name', display_order: 0, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_email', field_type: 'email_field', field_label: 'E-mail', system_column: 'email', display_order: 1, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_phone', field_type: 'phone', field_label: 'Telefone', system_column: 'phone', display_order: 2, is_visible_in_list: true, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_status', field_type: 'select', field_label: 'Status', system_column: 'status', display_order: 3, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: false, is_visible_in_detail: true },
  { field_name: 'sys_source', field_type: 'select', field_label: 'Fonte', system_column: 'source', display_order: 4, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_value', field_type: 'monetary', field_label: 'Valor', system_column: 'value', display_order: 5, is_visible_in_list: false, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_owner_id', field_type: 'user', field_label: 'Proprietário', system_column: 'owner_id', display_order: 6, is_visible_in_list: true, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_company_id', field_type: 'entity_reference', field_label: 'Empresa', system_column: 'company_id', display_order: 7, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_last_contact_at', field_type: 'date', field_label: 'Último Contato', system_column: 'last_contact_at', display_order: 8, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: false, is_visible_in_detail: true },
];

const DEALS_SYSTEM_SEEDS: DefaultFieldSeed[] = [
  { field_name: 'sys_title', field_type: 'text', field_label: 'Título', system_column: 'title', display_order: 0, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_description', field_type: 'textarea', field_label: 'Descrição', system_column: 'description', display_order: 1, is_visible_in_list: false, is_sortable: false, is_filterable: false, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_amount', field_type: 'monetary', field_label: 'Valor', system_column: 'amount', display_order: 2, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  // Derivado dos produtos pelo trigger update_deal_value_from_products — por isso fora do form de criação.
  { field_name: 'sys_contract_total', field_type: 'monetary', field_label: 'Valor total de contrato', system_column: 'contract_total', display_order: 3, is_visible_in_list: false, is_sortable: true, is_filterable: true, is_visible_in_add_form: false, is_visible_in_detail: true },
  { field_name: 'sys_stage_id', field_type: 'select', field_label: 'Etapa', system_column: 'stage_id', display_order: 4, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_probability', field_type: 'number', field_label: 'Probabilidade', system_column: 'probability', display_order: 5, is_visible_in_list: false, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_close_date', field_type: 'date', field_label: 'Data de Fechamento', system_column: 'close_date', display_order: 6, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_won_at', field_type: 'date', field_label: 'Data de Ganho', system_column: 'won_at', display_order: 7, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: false, is_visible_in_detail: true },
  { field_name: 'sys_lost_at', field_type: 'date', field_label: 'Data de Perda', system_column: 'lost_at', display_order: 8, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: false, is_visible_in_detail: true },
  { field_name: 'sys_lead_id', field_type: 'entity_reference', field_label: 'Lead', system_column: 'lead_id', display_order: 9, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_company_id', field_type: 'entity_reference', field_label: 'Empresa', system_column: 'company_id', display_order: 10, is_visible_in_list: true, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_owner_id', field_type: 'user', field_label: 'Proprietário', system_column: 'owner_id', display_order: 11, is_visible_in_list: true, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
];

const COMPANIES_SYSTEM_SEEDS: DefaultFieldSeed[] = [
  { field_name: 'sys_name', field_type: 'text', field_label: 'Nome', system_column: 'name', display_order: 0, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_domain', field_type: 'text', field_label: 'Domínio', system_column: 'domain', display_order: 1, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_email', field_type: 'email_field', field_label: 'E-mail', system_column: 'email', display_order: 2, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_phone', field_type: 'phone', field_label: 'Telefone', system_column: 'phone', display_order: 3, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_industry', field_type: 'select', field_label: 'Indústria', system_column: 'industry', display_order: 4, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_size', field_type: 'select', field_label: 'Porte', system_column: 'size', display_order: 5, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_location', field_type: 'text', field_label: 'Localização', system_column: 'location', display_order: 6, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_faturamento', field_type: 'text', field_label: 'Faturamento', system_column: 'faturamento', display_order: 7, is_visible_in_list: false, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_owner_id', field_type: 'user', field_label: 'Proprietário', system_column: 'owner_id', display_order: 8, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
];

const ACTIVITIES_SYSTEM_SEEDS: DefaultFieldSeed[] = [
  { field_name: 'sys_title', field_type: 'text', field_label: 'Título', system_column: 'title', display_order: 0, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_type', field_type: 'select', field_label: 'Tipo', system_column: 'type', display_order: 1, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_status', field_type: 'select', field_label: 'Status', system_column: 'status', display_order: 2, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: false, is_visible_in_detail: true },
  { field_name: 'sys_date', field_type: 'date', field_label: 'Data', system_column: 'date', display_order: 3, is_visible_in_list: true, is_sortable: true, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_description', field_type: 'textarea', field_label: 'Descrição', system_column: 'description', display_order: 4, is_visible_in_list: false, is_sortable: false, is_filterable: false, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_deal_id', field_type: 'entity_reference', field_label: 'Negócio', system_column: 'deal_id', display_order: 5, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_lead_id', field_type: 'entity_reference', field_label: 'Lead', system_column: 'lead_id', display_order: 6, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_company_id', field_type: 'entity_reference', field_label: 'Empresa', system_column: 'company_id', display_order: 7, is_visible_in_list: false, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
  { field_name: 'sys_assigned_to', field_type: 'user', field_label: 'Responsável', system_column: 'assigned_to', display_order: 8, is_visible_in_list: true, is_sortable: false, is_filterable: true, is_visible_in_add_form: true, is_visible_in_detail: true },
];

const DEFAULT_SYSTEM_SEEDS: Record<EntityType, DefaultFieldSeed[]> = {
  // Projetos não têm campos de sistema semeados: o módulo já nasceu com as colunas reais
  // (state, blocked_by, owner_id, due_date...) como colunas, não como campo personalizado.
  // Vazio aqui é a resposta certa — e o Record<> obriga a dizê-lo em vez de esquecer.
  work_items: [],
  // Contratos idem: `status`, `stage`, `payment_mode`, `health` e `tier` sao COLUNAS reais da
  // tabela, nao campo personalizado. O catalogo filtravel vive em `relationalFieldRegistry`.
  client_contracts: [],
  leads: LEADS_SYSTEM_SEEDS,
  deals: DEALS_SYSTEM_SEEDS,
  companies: COMPANIES_SYSTEM_SEEDS,
  activities: ACTIVITIES_SYSTEM_SEEDS,
};

/**
 * Gera os campos default do sistema para uma entidade.
 * Usado como fallback quando a migração ainda não rodou no banco.
 */
export function getDefaultSystemFields(entityType: EntityType, orgId: string): FieldDefinition[] {
  const groupId = `__default_group_${entityType}__`;
  const seeds = DEFAULT_SYSTEM_SEEDS[entityType] || [];
  return seeds.map((seed) => buildDefaultField(entityType, seed, orgId, groupId));
}

/**
 * Gera o grupo padrão do sistema para uma entidade (fallback frontend).
 */
export function getDefaultSystemGroup(entityType: EntityType, orgId: string): FieldGroup {
  const groupNames: Record<EntityType, string> = {
    leads: 'Informações do Lead',
    deals: 'Informações do Negócio',
    companies: 'Informações da Empresa',
    activities: 'Informações da Atividade',
    work_items: 'Informações do Projeto',
    client_contracts: 'Informações do Contrato',
  };
  return {
    id: `__default_group_${entityType}__`,
    org_id: orgId,
    entity_type: entityType,
    name: groupNames[entityType],
    display_order: 0,
    is_collapsible: true,
    is_default_collapsed: false,
    is_system: true,
    created_at: '',
    updated_at: '',
  };
}
